import { toast } from 'react-toastify';
import { tenantService } from '../services/api';

export type TenantPhoneLookupProfile = {
  phoneNumber?: string;
  username?: string;
  email?: string;
  address?: string;
  workingPlace?: string;
  emergencyContactName?: string;
  emergencyContactPhoneNumber?: string;
  photo?: string;
  idFrontSide?: string;
  idBackSide?: string;
};

export type TenantPhoneLookupResult = {
  found: boolean;
  source?: string;
  alreadyInHostel?: boolean;
  activeAtThisHostel?: boolean;
  blockPrefill?: boolean;
  existingUserId?: number | null;
  hint?: string;
  profile?: TenantPhoneLookupProfile | null;
};

function digitsOnly(phone: string): string {
  return phone.replace(/\D/g, '');
}

/** Merge lookup profile into form — only fills empty string fields. */
export function mergeTenantProfileFields<T extends Record<string, unknown>>(
  current: T,
  profile: TenantPhoneLookupProfile,
  keys: (keyof T)[],
): T {
  const next = { ...current };
  for (const key of keys) {
    const val = profile[key as keyof TenantPhoneLookupProfile];
    const cur = current[key];
    if (val && String(val).trim() && (cur == null || cur === '' || !String(cur).trim())) {
      (next as Record<string, unknown>)[key as string] = String(val).trim();
    }
  }
  return next;
}

export async function lookupAndPrefillTenantPhone(
  hostelId: number,
  phone: string,
  apply: (profile: TenantPhoneLookupProfile, meta: TenantPhoneLookupResult) => void,
): Promise<TenantPhoneLookupResult | null> {
  const digits = digitsOnly(phone);
  if (digits.length < 8) return null;

  try {
    const result = await tenantService.lookupTenantByPhone(hostelId, phone.trim());
    if (!result.found) return result;

    if (result.alreadyInHostel || result.blockPrefill) {
      if (result.hint) {
        toast.warning(result.hint);
      }
      return result;
    }

    if (result.profile) {
      apply(result.profile, result);
    }
    if (result.hint) {
      toast.info(result.hint);
    }
    return result;
  } catch {
    return null;
  }
}
